import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Car, CheckCircle2, TrendingUp, Inbox, Handshake, Plus, Pencil, Trash2, LogOut, X, Loader2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { fmtPrice, fmtKm, STATUS_LABELS } from "@/lib/constants";

const inputCls =
  "w-full h-11 rounded-xl bg-[#0d0d0d] border border-white/10 px-3 text-sm text-white placeholder:text-zinc-600 focus:border-[#FF1E27] outline-none";
const labelCls = "block font-spec text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5";

const EMPTY_FORM = {
  brand: "", model: "", year: new Date().getFullYear(), km: "", price: "", power: "",
  fuel: "Essence", transmission: "Manuelle", status: "disponible",
  critair: "1", warranty: "Garantie 3 mois boîte/moteur", history: "",
  description: "", options: "", imagesText: "",
};

const TABS = [
  { id: "inventory", label: "Catalogue", icon: Car, testid: "admin-tab-inventory" },
  { id: "leads", label: "Demandes clients", icon: Inbox, testid: "admin-tab-leads" },
  { id: "deposits", label: "Reprises / Dépôts", icon: Handshake, testid: "admin-tab-deposits" },
];

const fmtDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("inventory");
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [leads, setLeads] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [modal, setModal] = useState(null); // null | { mode: 'create' } | { mode: 'edit', vehicle }
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd);
        urls.push(data.url);
      }
      setForm((prev) => ({ ...prev, imagesText: [prev.imagesText, ...urls].filter(Boolean).join("\n") }));
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} ajoutée${urls.length > 1 ? "s" : ""}.`);
    } catch (err) {
      toast.error(formatApiError(err, "Échec de l'envoi des photos."));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) =>
    setForm((prev) => ({
      ...prev,
      imagesText: prev.imagesText.split("\n").filter((s) => s.trim()).filter((_, i) => i !== idx).join("\n"),
    }));

  const [dragIdx, setDragIdx] = useState(null);

  const moveImage = (from, to) => {
    if (from === null || to < 0) return;
    setForm((prev) => {
      const list = prev.imagesText.split("\n").filter((s) => s.trim());
      if (from >= list.length || to >= list.length) return prev;
      const [item] = list.splice(from, 1);
      list.splice(to, 0, item);
      return { ...prev, imagesText: list.join("\n") };
    });
  };

  const setCover = (idx) => moveImage(idx, 0);

  const load = async () => {
    try {
      const [s, v, l, d] = await Promise.all([
        api.get("/admin/stats"), api.get("/vehicles"), api.get("/leads"), api.get("/deposits"),
      ]);
      setStats(s.data);
      setVehicles(v.data);
      setLeads(l.data);
      setDeposits(d.data);
    } catch {
      toast.error("Erreur de chargement des données.");
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user === null) return <main className="min-h-screen pt-40 text-center text-zinc-500 font-spec uppercase tracking-widest text-sm">Vérification…</main>;
  if (user === false) return <Navigate to="/admin" replace />;

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setModal({ mode: "create" });
  };

  const openEdit = (v) => {
    setForm({
      brand: v.brand, model: v.model, year: v.year, km: v.km, price: v.price, power: v.power || "",
      fuel: v.fuel, transmission: v.transmission, status: v.status,
      critair: v.critair ? String(v.critair) : "", warranty: v.warranty || "", history: v.history || "",
      description: v.description || "", options: v.options || "", imagesText: (v.images || []).join("\n"),
    });
    setModal({ mode: "edit", vehicle: v });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      brand: form.brand, model: form.model,
      year: parseInt(form.year, 10), km: parseInt(form.km, 10), price: parseInt(form.price, 10),
      power: form.power ? parseInt(form.power, 10) : null,
      fuel: form.fuel, transmission: form.transmission, status: form.status,
      critair: form.critair ? parseInt(form.critair, 10) : null,
      warranty: form.warranty, history: form.history,
      description: form.description, options: form.options,
      images: form.imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (modal.mode === "create") {
        await api.post("/vehicles", payload);
        toast.success("Véhicule ajouté au catalogue.");
      } else {
        await api.put(`/vehicles/${modal.vehicle.id}`, payload);
        toast.success("Véhicule mis à jour.");
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Supprimer ${v.brand} ${v.model} ?`)) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success("Véhicule supprimé.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <main data-testid="admin-dashboard" className="pt-24 md:pt-28 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="font-spec text-xs uppercase tracking-[0.3em] text-[#FF1E27] mb-2">Espace Pro</p>
            <h1 className="font-display text-3xl font-black uppercase tracking-tight">Tableau de bord</h1>
          </div>
          <button data-testid="admin-logout-btn" onClick={logout}
            className="h-10 px-5 rounded-full border border-white/15 text-sm text-zinc-300 flex items-center gap-2 hover:border-[#FF1E27] hover:text-[#FF1E27] transition-colors">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div data-testid="admin-stats" className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-10">
            {[
              { icon: Car, label: "Véhicules", value: stats.total_vehicles },
              { icon: CheckCircle2, label: "Disponibles", value: stats.available },
              { icon: TrendingUp, label: "Vendus", value: stats.sold },
              { icon: Inbox, label: "Demandes", value: stats.leads },
              { icon: Handshake, label: "Reprises", value: stats.deposits },
            ].map((s) => (
              <div key={s.label} className="bg-[#141414] border border-white/10 rounded-2xl p-4">
                <s.icon className="w-4 h-4 text-[#FF1E27] mb-2" />
                <p className="font-display text-2xl font-black">{s.value}</p>
                <p className="font-spec text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-white/10 pb-px overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={t.testid} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === t.id ? "border-[#FF1E27] text-white" : "border-transparent text-zinc-500 hover:text-white"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* INVENTORY */}
        {tab === "inventory" && (
          <div>
            <button data-testid="admin-add-vehicle-btn" onClick={openCreate}
              className="mb-6 h-11 px-6 rounded-full bg-[#FF1E27] text-white text-sm font-semibold flex items-center gap-2 hover:bg-[#E63946] transition-colors">
              <Plus className="w-4 h-4" /> Ajouter un véhicule
            </button>
            <div className="space-y-3">
              {vehicles.map((v) => (
                <div key={v.id} data-testid={`admin-vehicle-row-${v.id}`}
                  className="bg-[#141414] border border-white/10 rounded-2xl p-4 flex flex-wrap items-center gap-4">
                  <div className="w-24 aspect-[16/10] rounded-lg overflow-hidden bg-[#1A1A1A] shrink-0">
                    {v.images?.[0] && <img src={v.images[0]} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-display font-bold">{v.brand} {v.model}</p>
                    <p className="text-xs text-zinc-500 mt-1">{v.year} · {fmtKm(v.km)} · {v.fuel} · Crit'Air {v.critair ?? "—"}</p>
                  </div>
                  <p className="font-display font-black text-[#FF1E27]">{fmtPrice(v.price)}</p>
                  <span className="text-[11px] font-spec uppercase tracking-widest text-zinc-400 border border-white/10 rounded-full px-2.5 py-1">
                    {STATUS_LABELS[v.status]}
                  </span>
                  <div className="flex gap-2">
                    <button data-testid={`admin-edit-vehicle-${v.id}`} onClick={() => openEdit(v)} aria-label="Modifier"
                      className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-zinc-300 hover:border-[#FF1E27] hover:text-white transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`admin-delete-vehicle-${v.id}`} onClick={() => remove(v)} aria-label="Supprimer"
                      className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-zinc-300 hover:border-red-500 hover:text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {vehicles.length === 0 && <p className="text-zinc-500 text-sm">Aucun véhicule. Ajoutez votre première voiture.</p>}
            </div>
          </div>
        )}

        {/* LEADS */}
        {tab === "leads" && (
          <div className="space-y-3" data-testid="admin-leads-list">
            {leads.map((l) => (
              <div key={l.id} className="bg-[#141414] border border-white/10 rounded-2xl p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <p className="font-display font-bold">{l.name} <span className="text-zinc-500 font-normal text-sm">· {l.phone}</span></p>
                  <span className="text-xs text-zinc-500 font-spec">{fmtDate(l.created_at)}</span>
                </div>
                <p className="text-xs font-spec uppercase tracking-widest text-[#FF1E27] mb-2">{l.subject}</p>
                {l.vehicle_label && <p className="text-xs text-zinc-500 mb-2">Véhicule : {l.vehicle_label}</p>}
                <p className="text-sm text-zinc-300 leading-relaxed">{l.message}</p>
                {l.email && <a href={`mailto:${l.email}`} className="inline-block mt-2 text-xs text-zinc-400 hover:text-white">{l.email}</a>}
              </div>
            ))}
            {leads.length === 0 && <p className="text-zinc-500 text-sm">Aucune demande pour le moment.</p>}
          </div>
        )}

        {/* DEPOSITS */}
        {tab === "deposits" && (
          <div className="space-y-3" data-testid="admin-deposits-list">
            {deposits.map((d) => (
              <div key={d.id} className="bg-[#141414] border border-white/10 rounded-2xl p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <p className="font-display font-bold">{d.brand} {d.model}</p>
                  <span className="text-xs text-zinc-500 font-spec">{fmtDate(d.created_at)}</span>
                </div>
                <p className="text-xs text-zinc-500 mb-2">
                  {d.name} · {d.phone} {d.email && `· ${d.email}`} · {d.year} · {fmtKm(d.km)}
                  {d.desired_price ? ` · Prix souhaité : ${fmtPrice(d.desired_price)}` : ""}
                </p>
                {d.description && <p className="text-sm text-zinc-300 leading-relaxed">{d.description}</p>}
              </div>
            ))}
            {deposits.length === 0 && <p className="text-zinc-500 text-sm">Aucune proposition de revente pour le moment.</p>}
          </div>
        )}
      </div>

      {/* MODAL FORM */}
      {modal && (
        <div data-testid="admin-vehicle-modal" className="fixed inset-0 z-[60] bg-black/90 overflow-y-auto p-4 flex items-start justify-center">
          <div className="w-full max-w-3xl bg-[#141414] border border-white/10 rounded-2xl p-6 sm:p-8 my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-extrabold uppercase tracking-tight">
                {modal.mode === "create" ? "Ajouter un véhicule" : `Modifier — ${modal.vehicle.brand} ${modal.vehicle.model}`}
              </h2>
              <button data-testid="admin-modal-close-btn" onClick={() => setModal(null)} aria-label="Fermer"
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-zinc-300 hover:border-[#FF1E27]">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={save} className="grid sm:grid-cols-2 gap-4" data-testid="admin-vehicle-form">
              <div><label className={labelCls}>Marque *</label>
                <input data-testid="vehicle-form-brand" required value={form.brand} onChange={set("brand")} className={inputCls} placeholder="BMW" /></div>
              <div><label className={labelCls}>Modèle *</label>
                <input data-testid="vehicle-form-model" required value={form.model} onChange={set("model")} className={inputCls} placeholder="Série 3 320d (E90)" /></div>
              <div><label className={labelCls}>Année *</label>
                <input data-testid="vehicle-form-year" required type="number" min="1980" max="2026" value={form.year} onChange={set("year")} className={inputCls} /></div>
              <div><label className={labelCls}>Kilométrage *</label>
                <input data-testid="vehicle-form-km" required type="number" min="0" value={form.km} onChange={set("km")} className={inputCls} /></div>
              <div><label className={labelCls}>Prix TTC (€) *</label>
                <input data-testid="vehicle-form-price" required type="number" min="0" value={form.price} onChange={set("price")} className={inputCls} /></div>
              <div><label className={labelCls}>Puissance (ch)</label>
                <input data-testid="vehicle-form-power" type="number" min="0" value={form.power} onChange={set("power")} className={inputCls} /></div>
              <div><label className={labelCls}>Énergie *</label>
                <select data-testid="vehicle-form-fuel" value={form.fuel} onChange={set("fuel")} className={inputCls}>
                  {["Essence", "Diesel", "Hybride", "Électrique", "GPL"].map((f) => <option key={f}>{f}</option>)}
                </select></div>
              <div><label className={labelCls}>Boîte *</label>
                <select data-testid="vehicle-form-transmission" value={form.transmission} onChange={set("transmission")} className={inputCls}>
                  {["Manuelle", "Automatique", "Automatique DSG", "Automatique PDK", "Automatique S-Tronic"].map((t) => <option key={t}>{t}</option>)}
                </select></div>
              <div><label className={labelCls}>Vignette Crit'Air</label>
                <select data-testid="vehicle-form-critair" value={form.critair} onChange={set("critair")} className={inputCls}>
                  <option value="">Non concerné</option>
                  {[1, 2, 3, 4, 5].map((c) => <option key={c} value={c}>Crit'Air {c}</option>)}
                </select></div>
              <div><label className={labelCls}>Statut</label>
                <select data-testid="vehicle-form-status" value={form.status} onChange={set("status")} className={inputCls}>
                  <option value="disponible">Disponible</option>
                  <option value="reserve">Réservé</option>
                  <option value="vendu">Vendu</option>
                </select></div>
              <div className="sm:col-span-2"><label className={labelCls}>Garantie (durée & type)</label>
                <input data-testid="vehicle-form-warranty" value={form.warranty} onChange={set("warranty")} className={inputCls} placeholder="Garantie 12 mois moteur/boîte/pont" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Provenance & historique</label>
                <input data-testid="vehicle-form-history" value={form.history} onChange={set("history")} className={inputCls} placeholder="Factures d'entretien disponibles, double des clés, contrôle technique OK" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Description</label>
                <textarea data-testid="vehicle-form-description" rows={3} value={form.description} onChange={set("description")}
                  className="w-full rounded-xl bg-[#0d0d0d] border border-white/10 px-3 py-2.5 text-sm text-white focus:border-[#FF1E27] outline-none resize-none" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Options & équipements</label>
                <textarea data-testid="vehicle-form-options" rows={2} value={form.options} onChange={set("options")}
                  className="w-full rounded-xl bg-[#0d0d0d] border border-white/10 px-3 py-2.5 text-sm text-white focus:border-[#FF1E27] outline-none resize-none" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Photos du véhicule</label>
                <label data-testid="vehicle-form-upload-btn"
                  className="flex items-center justify-center gap-2 h-20 rounded-xl border-2 border-dashed border-white/15 bg-[#0d0d0d] text-sm text-zinc-400 cursor-pointer hover:border-[#FF1E27]/50 hover:text-white transition-colors">
                  <input data-testid="vehicle-form-file-input" type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin text-[#FF1E27]" /> : <Plus className="w-4 h-4 text-[#FF1E27]" />}
                  {uploading ? "Envoi en cours…" : "Ajouter des photos (téléphone ou ordinateur)"}
                </label>
                {form.imagesText.trim() && (
                  <>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {form.imagesText.split("\n").filter((s) => s.trim()).map((u, i) => (
                        <div key={u} draggable
                          data-testid={`vehicle-form-photo-thumb-${i}`}
                          onDragStart={() => setDragIdx(i)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => { moveImage(dragIdx, i); setDragIdx(null); }}
                          className={`relative w-24 aspect-[16/10] rounded-lg overflow-hidden border-2 cursor-grab active:cursor-grabbing ${i === 0 ? "border-[#FF1E27]" : "border-white/10"}`}>
                          <img src={u} alt={`Photo ${i + 1}`} loading="lazy" decoding="async" draggable={false} className="w-full h-full object-cover" />
                          {i === 0 && (
                            <span className="absolute bottom-1 left-1 text-[9px] font-spec uppercase tracking-wider bg-[#FF1E27] text-white rounded px-1.5 py-0.5">Couverture</span>
                          )}
                          {i !== 0 && (
                            <button type="button" data-testid={`vehicle-form-cover-btn-${i}`} onClick={() => setCover(i)}
                              title="Définir comme photo principale" aria-label="Définir comme photo principale"
                              className="absolute top-1 left-1 w-6 h-6 rounded-full bg-black/80 border border-white/20 flex items-center justify-center text-zinc-300 hover:text-[#FF1E27] hover:border-[#FF1E27]">
                              <CheckCircle2 className="w-3 h-3" />
                            </button>
                          )}
                          <button type="button" data-testid={`vehicle-form-remove-photo-${i}`} onClick={() => removeImage(i)} aria-label="Retirer cette photo"
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/80 border border-white/20 flex items-center justify-center text-white hover:border-red-500 hover:text-red-400">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-zinc-500">Glissez les photos pour les réordonner, ou touchez la coche pour définir la photo principale. La première photo est la couverture de l'annonce.</p>
                  </>
                )}
                <textarea data-testid="vehicle-form-images" rows={2} value={form.imagesText} onChange={set("imagesText")}
                  placeholder={"Ou collez des URLs — une par ligne"}
                  className="mt-3 w-full rounded-xl bg-[#0d0d0d] border border-white/10 px-3 py-2.5 text-sm text-white font-spec focus:border-[#FF1E27] outline-none resize-none" /></div>

              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button data-testid="vehicle-form-submit-btn" type="submit" disabled={saving}
                  className="h-11 px-8 rounded-full bg-[#FF1E27] text-white text-sm font-semibold flex items-center gap-2 hover:bg-[#E63946] transition-colors disabled:opacity-60">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {modal.mode === "create" ? "Publier le véhicule" : "Enregistrer"}
                </button>
                <button type="button" data-testid="vehicle-form-cancel-btn" onClick={() => setModal(null)}
                  className="h-11 px-6 rounded-full border border-white/15 text-sm text-zinc-300 hover:border-white/40 transition-colors">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
